import { timeAgo } from '../utils/events'
import { Skeleton } from './Skeleton'

const TheftIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.36 3.59L1.99 17.5A1.91 1.91 0 0 0 3.63 20.4h16.74a1.91 1.91 0 0 0 1.64-2.9L13.64 3.6a1.91 1.91 0 0 0-3.28 0z" />
    <path d="M12 9v4M12 17h.01" />
  </svg>
)

const RefuelIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21V7a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v14M3 12h10M13 9h3l3 3v6a1.5 1.5 0 0 1-3 0v-3h-3" />
  </svg>
)

// Wrench glyph — distinct from the theft warning triangle so a glance is enough
// to know it's a hardware tamper, not a fuel drop on its own.
const TamperIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.7 6.3a4 4 0 1 0 5 5l-1.4 1.4 4 4-2.8 2.8-4-4-1.4 1.4a4 4 0 0 1-5-5l2.5 2.5 2.6-2.6L12 4.7 14.7 6.3z" />
  </svg>
)

// Crescent moon for after-hours movement events — the same icon the
// dashboard badge uses so the visual language stays consistent.
const MoonIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
)

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="var(--tg-status-online)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M9 12l2 2 4-4" />
  </svg>
)

export default function EventFeed({ events, loading }) {
  return (
    <div className="feed-card">
      <div className="feed-head">
        <h2>Event feed</h2>
        <span style={{ fontSize: 'var(--tg-text-sm)', color: 'var(--tg-text-tertiary)' }}>last 24h</span>
      </div>

      {loading && [0, 1, 2].map(i => (
        <div key={i} className="feed-ev">
          <Skeleton width={32} height={32} radius={8} style={{ flexShrink: 0 }} />
          <div className="feed-body" style={{ flex: 1 }}>
            <Skeleton width="70%" height={13} style={{ marginBottom: 6 }} />
            <Skeleton width="45%" height={11} />
          </div>
          <Skeleton width={34} height={11} />
        </div>
      ))}

      {!loading && events.length === 0 && (
        <div style={{ padding: 'var(--tg-space-6) var(--tg-space-4)', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
            <CheckIcon />
          </div>
          <div style={{ color: 'var(--tg-text-primary)', fontSize: 'var(--tg-text-sm)', fontWeight: 'var(--tg-weight-medium)' }}>
            No events in the last 24h
          </div>
          <div style={{ color: 'var(--tg-text-muted)', fontSize: 'var(--tg-text-xs)', marginTop: 3 }}>
            All clear — no theft or unusual activity detected
          </div>
        </div>
      )}

      {events.slice(0, 6).map((ev, i) => {
        const isGeo = ev.type === 'geofence_exit' || ev.type === 'geofence_enter'
        const isTamper = ev.type === 'tamper'
        const isAfterHours = ev.type === 'after_hours'
        // Tamper and after-hours each have their own visual lane so they
        // don't read as ordinary theft / refuel events at a glance.
        const rowStyle = isTamper
          ? { background: 'rgba(234, 88, 12, 0.06)', borderLeft: '3px solid #ea580c' }
          : isAfterHours
          ? { background: 'rgba(99, 102, 241, 0.05)', borderLeft: '3px solid #6366f1' }
          : undefined
        const iconKind = ev.type === 'theft' || ev.type === 'geofence_exit'
          ? 'theft'
          : isTamper ? 'tamper'
          : isAfterHours ? 'after_hours'
          : 'refuel'
        const iconStyle = isTamper
          ? { background: '#fed7aa', color: '#9a3412' }
          : isAfterHours
          ? { background: '#e0e7ff', color: '#3730a3' }
          : undefined
        return (
          <div key={i} className="feed-ev" style={rowStyle}>
            <div className={`feed-ic ${iconKind}`} style={iconStyle}>
              {isTamper ? <TamperIcon />
                : isAfterHours ? <MoonIcon />
                : iconKind === 'theft' ? <TheftIcon />
                : <RefuelIcon />}
            </div>
            <div className="feed-body">
              <div className="feed-title" style={isTamper ? { color: '#9a3412' } : isAfterHours ? { color: '#3730a3' } : undefined}>
                {isTamper ? 'Sensor tampered — ' + ev.truck
                  : isAfterHours ? 'After-hours movement — ' + ev.truck
                  : ev.type === 'theft' ? 'Theft detected — ' + ev.truck
                  : isGeo ? (ev.truck + ' ' + (ev.type === 'geofence_exit' ? 'left' : 'entered') + ' ' + ev.fence)
                  : 'Refuel — ' + ev.truck}{ev.side ? ' · ' + (ev.side === 'L' ? 'Left' : 'Right') : ''}
              </div>
              <div className="feed-sub">
                {isTamper
                  ? 'BLE disconnect + ' + ev.liters.toFixed(0) + ' L drop · €' + ev.eur + ' loss'
                  : isAfterHours
                  ? Math.round(ev.speed || 0) + ' km/h outside operating hours'
                  : ev.type === 'theft'
                  ? '-' + ev.liters.toFixed(0) + ' L while stationary · €' + ev.eur + ' loss'
                  : isGeo ? 'Geofence ' + (ev.type === 'geofence_exit' ? 'exit' : 'entry')
                  : '+' + ev.liters.toFixed(0) + ' L added · €' + ev.eur}
              </div>
            </div>
            <div className="feed-time">{timeAgo(ev.ts)}</div>
          </div>
        )
      })}
    </div>
  )
}
